import { CellConfig } from "shared/components/UniversalTable/components";
import { FileText, Building, SquaresFour, Trash, Receipt } from "@phosphor-icons/react";
import { Checkbox } from "components/ui/checkbox";
import { formatCurrency } from "shared/utils/currency";
import { Avatar } from "@heroui/react";
import { getUserImageUrlById, getUserName } from "shared/utils/users";
import { resolveDocumentVat, VAT_UNKNOWN } from "shared/utils/invoiceVat";
import { VatWarningBadge } from "components/renovationInvoices/VatWarningBadge";
import { DocumentSignStatus } from "shared/types/renovation";

interface CreateDocumentCellsParams {
  document: any;
  handlers: {
    onDocumentClick?: (document: any) => void;
    onProjectClick?: (projectId: number) => void;
    onStatusClick?: (document: any) => void;
    onDateClick?: (document: any) => void;
    onDeleteClick?: (document: any) => void;
    /** Opent de bestelbon-kiezer voor deze factuur. */
    onPurchaseOrderClick?: (document: any) => void;
    /** Opent de offerte-kiezer voor deze factuur. */
    onQuoteClick?: (document: any) => void;
    /** Opent de renovatieticket-kiezer voor dit document. */
    onRenoTicketClick?: (document: any) => void;
  };
  users?: any[];
  deletingDocumentIds?: Set<number>;
  /**
   * Row selection context supplied by UniversalTable. When present a leading
   * checkbox cell is rendered so rows can be bulk-selected (e.g. to delete a
   * batch of over-imported invoices).
   */
  rowContext?: {
    isSelected: boolean;
    onToggleSelect: (selected: boolean) => void;
  };
}

/**
 * Odoo payment_state → Dutch label + badge styling. Driven by the daily Odoo
 * sync (account.move.payment_state). Unknown/empty → no badge ("—").
 */
const PAYMENT_STATUS_CONFIG: Record<
  string,
  { label: string; className: string; title: string }
> = {
  paid: {
    label: "Betaald",
    className: "border-green-200 bg-green-50 text-green-700",
    title: "Volledig betaald in Odoo",
  },
  in_payment: {
    label: "In betaling",
    className: "border-blue-200 bg-blue-50 text-blue-700",
    title: "Betaling geregistreerd, nog niet afgepunt in Odoo",
  },
  partial: {
    label: "Gedeeltelijk",
    className: "border-amber-300 bg-amber-50 text-amber-700",
    title: "Gedeeltelijk betaald in Odoo",
  },
  not_paid: {
    label: "Niet betaald",
    className: "border-red-200 bg-red-50 text-red-700",
    title: "Nog niet betaald in Odoo",
  },
  reversed: {
    label: "Tegengeboekt",
    className: "border-neutral-200 bg-neutral-50 text-neutral-secondary-mid",
    title: "Tegengeboekt in Odoo",
  },
};

/**
 * Creates cell configurations for a single document row
 * Similar to helpdeskCellFactory but for renovation documents
 */
export const createDocumentCells = ({
  document,
  handlers,
  users = [],
  deletingDocumentIds = new Set(),
  rowContext,
}: CreateDocumentCellsParams): CellConfig[] => {
  const isDeleting = deletingDocumentIds.has(document.id);
  // Calculate total amount from document lines.
  // DocumentLine.totalInclVat is nullable in the schema while totalExclVat is
  // not — fall back to totalExclVat so imported invoices that only populated
  // the excl-VAT field aren't shown as €0,00.
  // Math.abs because the importInvoice code path at renovation.service.ts:3248
  // stores invoice line totals as NEGATIVE values (sign-flipped from the Odoo
  // move line) while the path at line 2957 stores them as-is.  Display the
  // magnitude so totals are consistent and positive regardless of which
  // import code originally wrote the row.  Matches the BE stats query.
  const totalAmount =
    document.lines?.reduce(
      (sum: number, line: any) =>
        sum + Math.abs(Number(line.totalInclVat ?? line.totalExclVat ?? 0) || 0),
      0,
    ) || 0;

  // Creditnota (Odoo `in_refund`, documentnummer RLF/…): die verlaagt de kosten, dus
  // hoort ze als NEGATIEF bedrag in het rood te staan. Zonder dit onderscheid stond een
  // creditnota van € 50.000 er als een gewone factuur van € 50.000 bij, in dezelfde
  // kleur — niet te onderscheiden van wat je moet betalen. De backend-aggregaties
  // trekken creditnota's al af via dezelfde vlag; de weergave volgt nu.
  const isCreditNote = document.isCreditNote === true;
  /** Bedrag met het juiste teken: negatief bij een creditnota. */
  const signed = (value: number) => (isCreditNote ? -Math.abs(value) : value);
  const amountClass = isCreditNote ? "text-red-600" : "text-neutral-primary";

  // BTW-uitsplitsing over alle regels van het document. Let op: regels die vóór
  // de btw-uitsplitsing zijn geïmporteerd hebben vatPercentage/vatAmount NULL of
  // 0 opgeslagen; die tonen "—" tot de backfill is gelopen.
  const vat = resolveDocumentVat(document.lines);

  // Get primary project (first project in array)
  const primaryProject = document.projects?.[0];

  // Get primary part (from first document line)
  const primaryPart = document.lines?.[0]?.part;

  // Get building from primary project
  const building = primaryProject?.building;

  // ─── Kostenplaatsen aggregation ────────────────────────────────────────────
  // Two data sources, in priority order:
  //   1) DocumentLine.analyticDistributions — de verdeling op de regel zelf.
  //      Bij een offerte handmatig ingegeven, bij een geïmporteerde factuur
  //      overgenomen uit de kostenplaatsboeking in Odoo of afgeleid uit de
  //      voorziene kostenplaatsen van het projectonderdeel (`source` zegt
  //      welke van de drie — zie renovation-kostenplaats.service.ts).
  //      Percentages stored as fractions (0.5 = 50%).
  //   2) DocumentLine.part.investmentAssignments[].unitDistributions — fallback
  //      voor facturen waarvan de verdeling nog niet weggeschreven is.
  //      Percentages stored as 0-100 numbers per InvestmentAssignmentUnitDistribution.
  //      For a part with multiple assignments we compute the effective per-unit
  //      share weighted by each assignment's assignedAmount, producing a single
  //      0-100 distribution that sums to 100%.
  //
  // Elke regel weegt mee voor HAAR aandeel in het factuurtotaal. Zonder die
  // weging telde een factuur van twee regels die elk 100% op dezelfde unit
  // boeken op tot 200%, en dan is de kolom onbruikbaar voor de vraag "hoeveel
  // is er in deze unit geïnvesteerd". Bij één regel — het gros — verandert er
  // niets. Regels zonder bedrag wegen gelijk, zodat een factuur waarvan de
  // bedragen ontbreken toch een verdeling toont.
  const partDistributionCache = new Map<
    number,
    Array<{ unitId: string; unitName: string; percentage: number }>
  >();

  const computePartDistribution = (
    part: any
  ): Array<{ unitId: string; unitName: string; percentage: number }> => {
    if (!part) return [];
    if (typeof part.id === "number" && partDistributionCache.has(part.id)) {
      return partDistributionCache.get(part.id)!;
    }
    const assignments = part.investmentAssignments ?? [];
    const totalAssigned = assignments.reduce(
      (s: number, a: any) => s + (Number(a.assignedAmount) || 0),
      0
    );

    const map: Record<string, { unitId: string; unitName: string; percentage: number }> = {};
    for (const asg of assignments) {
      const assigned = Number(asg.assignedAmount) || 0;
      const weightShare = totalAssigned > 0 ? assigned / totalAssigned : 1 / (assignments.length || 1);
      for (const ud of asg.unitDistributions ?? []) {
        const unitId = ud.unit?.id ?? String(ud.unitId ?? "");
        if (!unitId) continue;
        const unitName = ud.unit?.unitOdooId ?? ud.unit?.name ?? unitId;
        const pct = Number(ud.percentage) || 0;
        if (!map[unitId]) map[unitId] = { unitId, unitName, percentage: 0 };
        map[unitId].percentage += pct * weightShare;
      }
    }
    const result = Object.values(map);
    if (typeof part.id === "number") partDistributionCache.set(part.id, result);
    return result;
  };

  /** Label van een kostenplaats: de Odoo-naam waar de boekhouding op boekt. */
  const kostenplaatsLabel = (unit: any, unitId: string) =>
    unit?.unitOdooId ?? unit?.odooId ?? unit?.name ?? unitId;

  const documentLines = document.lines ?? [];
  const lineWeights = (() => {
    const amounts = documentLines.map(
      (line: any) =>
        Math.abs(Number(line.totalExclVat ?? line.totalInclVat ?? 0) || 0),
    );
    const total = amounts.reduce((s: number, a: number) => s + a, 0);
    if (total > 0) return amounts.map((a: number) => a / total);
    return documentLines.map(() => 1 / (documentLines.length || 1));
  })();

  const sumMap: Record<
    string,
    { unitId: string; unitName: string; percentage: number; sources: Set<string> }
  > = {};
  const addToSum = (
    unitId: string,
    unitName: string,
    displayPercent: number,
    source: string,
  ) => {
    if (!unitId) return;
    if (!sumMap[unitId])
      sumMap[unitId] = { unitId, unitName, percentage: 0, sources: new Set() };
    sumMap[unitId].percentage += displayPercent;
    sumMap[unitId].sources.add(source);
  };

  documentLines.forEach((line: any, lineIndex: number) => {
    const weight = lineWeights[lineIndex] ?? 0;
    const ownDistributions = line.analyticDistributions ?? [];
    if (ownDistributions.length > 0) {
      // Source 1: line-level fractions
      for (const d of ownDistributions) {
        const unitId = d.unit?.id ?? String(d.unitId ?? "");
        if (!unitId) continue;
        const pct = Number(d.percentage) || 0;
        addToSum(
          unitId,
          kostenplaatsLabel(d.unit, unitId),
          pct * 100 * weight,
          d.source ?? "manual",
        );
      }
      return;
    }
    // Source 2: derived from the line's project part
    const partDistribution = computePartDistribution(line.part);
    for (const pd of partDistribution) {
      addToSum(pd.unitId, pd.unitName, pd.percentage * weight, "assignment");
    }
  });

  const analyticDistributions = Object.values(sumMap);

  /**
   * Waar de kostenplaats vandaan komt, voor de tooltip. Voorkomt de vraag
   * "waarom staat hier iets anders dan in de boekhouding": je ziet meteen of
   * de verdeling uit Odoo komt of nog een afleiding uit het project is.
   */
  const KOSTENPLAATS_SOURCE_LABEL: Record<string, string> = {
    odoo: "uit de boekhouding (Odoo)",
    project: "afgeleid uit het projectonderdeel",
    assignment: "afgeleid uit de budgettoewijzingen",
    manual: "handmatig ingesteld",
  };
  const describeSources = (sources: Set<string>) =>
    Array.from(sources)
      .map((s) => KOSTENPLAATS_SOURCE_LABEL[s] ?? s)
      .join(" + ");

  // ─── Boekhouding versus renovatieproject ───────────────────────────────────
  // De kostenplaats hierboven is die van de BOEKHOUDING zodra Odoo er een heeft,
  // en die wijkt vaak af van wat op het renovatieproject voorzien is. Groen =
  // de boekhouding boekt op een voorziene kostenplaats, rood = niet. Zonder
  // deze kleur moest je het project openen om te weten of de boeking klopt.
  //
  // Wat "voorzien" is, per documentregel, in dezelfde rangorde als de backend
  // (renovation-kostenplaats.service.ts): de voorziene kostenplaatsen van het
  // projectonderdeel, anders de budgettoewijzingen van dat onderdeel, anders
  // die van het project.
  const expectedUnits = new Map<string, string>(); // unitId → label

  const projectProvisional = (document.projects ?? []).flatMap(
    (project: any) => project?.provisionalUnits ?? [],
  );

  const addExpected = (unitId: string, label: string) => {
    if (!unitId || expectedUnits.has(unitId)) return;
    expectedUnits.set(unitId, label);
  };

  const addProvisionalLinks = (links: any[]) => {
    for (const link of links ?? []) {
      const unitId = link?.unit?.id ?? String(link?.unitId ?? "");
      if (!unitId) continue;
      addExpected(unitId, kostenplaatsLabel(link?.unit, unitId));
    }
  };

  documentLines.forEach((line: any) => {
    const partProvisional = line.part?.provisionalUnits ?? [];
    if (partProvisional.length > 0) {
      addProvisionalLinks(partProvisional);
      return;
    }
    const partDistribution = computePartDistribution(line.part);
    if (partDistribution.length > 0) {
      partDistribution.forEach((pd) => addExpected(pd.unitId, pd.unitName));
      return;
    }
    addProvisionalLinks(projectProvisional);
  });

  /**
   * Een verdeling die zélf uit het project is afgeleid ('project' /
   * 'assignment') zou per definitie altijd overeenkomen. Groen kleuren zou dan
   * suggereren dat de boekhouding bevestigd is, terwijl die net níets geboekt
   * heeft. Enkel een kostenplaats uit de boekhouding of een handmatig
   * ingestelde valt dus te vergelijken.
   */
  const isComparable = (sources: Set<string>) =>
    Array.from(sources).some((s) => s === "odoo" || s === "manual");

  type KostenplaatsMatch = "match" | "mismatch" | "unknown";
  const matchStatus = (unitId: string, sources: Set<string>): KostenplaatsMatch => {
    if (!expectedUnits.size || !isComparable(sources)) return "unknown";
    return expectedUnits.has(unitId) ? "match" : "mismatch";
  };

  const MATCH_STYLE: Record<KostenplaatsMatch, string> = {
    match: "border-green-200 bg-green-50 text-green-700",
    mismatch: "border-red-200 bg-red-50 text-red-700",
    unknown: "border-neutral-200 bg-neutral-50 text-neutral-secondary-mid",
  };

  const expectedLabels = Array.from(expectedUnits.values());
  const describeExpected = () =>
    expectedLabels.length
      ? `voorzien op het renovatieproject: ${expectedLabels.join(", ")}`
      : "geen voorziene kostenplaatsen op het renovatieproject";

  const MATCH_TOOLTIP: Record<KostenplaatsMatch, string> = {
    match: `komt overeen met het renovatieproject (${describeExpected()})`,
    mismatch: `WIJKT AF van het renovatieproject (${describeExpected()})`,
    unknown: "niets om mee te vergelijken",
  };

  // Voorziene kostenplaatsen waar de boekhouding niets op geboekt heeft. Enkel
  // relevant wanneer er iets te vergelijken viel: anders is het geen afwijking
  // maar gewoon een factuur die haar kostenplaats nog uit het project haalt.
  const heeftVergelijkbareBoeking = analyticDistributions.some((d) =>
    isComparable(d.sources),
  );
  const nietGeboekt = heeftVergelijkbareBoeking
    ? Array.from(expectedUnits.entries()).filter(
        ([unitId]) => !sumMap[unitId],
      )
    : [];

  // CTA target: when no kostenplaatsen could be derived but at least one line
  // references a project part, point the user at that part so they can configure
  // its budget/unit distribution. Picks the first line with a part that has a
  // resolvable project id.
  const missingKostenplaatsenCta = (() => {
    if (analyticDistributions.length > 0) return null;
    for (const line of document.lines ?? []) {
      const part = line.part;
      if (!part) continue;
      const projectId =
        part.project?.id || part.projectId || part.renovationProjectId;
      if (projectId && part.id) {
        return {
          href: `/renovation/${projectId}/part/${part.id}`,
          label: `RENO-${projectId}-O${part.id}`,
        };
      }
    }
    return null;
  })();

  // Get responsible user ID from primary project
  const responsibleUserId = primaryProject?.responsibleUserId;

  // Format date
  const formatDate = (date?: Date | string) => {
    if (!date) return "-";
    const dateObj = date instanceof Date ? date : new Date(date);
    return dateObj.toLocaleDateString("nl-BE");
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case "Goedgekeurd":
        return "text-green-700 bg-green-50 border-green-200";
      case "Afgewezen":
        return "text-red-700 bg-red-50 border-red-200";
      case "Ontvangen":
        return "text-blue-700 bg-blue-50 border-blue-200";
      case "Concept":
        return "text-gray-700 bg-gray-50 border-gray-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  // Get type color
  const getTypeColor = (type: string) => {
    switch (type) {
      case "Offerte":
        return "text-moss-primary bg-moss-primary/10 border-moss-primary/30";
      case "Factuur":
        return "text-brick-primary bg-brick-primary/10 border-brick-primary/30";
      case "Bestelbon":
        return "text-blue-700 bg-blue-50 border-blue-200";
      case "Contract":
        return "text-purple-700 bg-purple-50 border-purple-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  return [
    // Selection checkbox — only rendered when the table provides selection
    // context. `stopPropagation` keeps a row-click from also toggling it.
    {
      id: "checkbox",
      colSpan: 1,
      align: "center",
      content: rowContext ? (
        <Checkbox
          checked={rowContext.isSelected}
          onCheckedChange={(checked) => rowContext.onToggleSelect(checked === true)}
          onClick={(e) => e.stopPropagation()}
          aria-label="Selecteer document"
        />
      ) : null,
    },

    // Document Number (always clickable to open document)
    {
      id: "documentNumber",
      content: (
        <div className="flex items-center gap-2">
          <FileText
            size={16}
            weight="duotone"
            className="text-moss-primary shrink-0"
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (handlers.onDocumentClick) {
                handlers.onDocumentClick(document);
              }
            }}
            className="text-moss-primary cursor-pointer text-left font-medium hover:underline"
            type="button"
            title="Open document"
          >
            {document.documentNumber || "Document openen"}
          </button>
          {/* Waarom de bedragen op deze rij negatief en rood staan. */}
          {isCreditNote && (
            <span
              className="shrink-0 rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[10px] font-medium text-red-700"
              title="Creditnota — wordt van de renovatiekost afgetrokken"
            >
              Creditnota
            </span>
          )}
        </div>
      ),
    },

    // Type
    {
      id: "type",
      content: (
        <div className="flex flex-wrap items-center gap-1">
          <span
            className={`rounded border px-2 py-1 text-xs font-medium ${getTypeColor(
              document.type,
            )}`}
          >
            {document.type}
          </span>
          {/* Getekend is de reden waarom een bestelbon opgeladen wordt, dus hoort
              het in de lijst te staan en niet enkel in het detailvenster. De
              volledige status (door wie) staat in de tooltip — in de kolom past
              enkel "Getekend". */}
          {document.signedStatus &&
            document.signedStatus !== DocumentSignStatus.NOT_SIGNED && (
              <span
                className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700"
                title={document.signedStatus}
              >
                Getekend
              </span>
            )}
        </div>
      ),
    },

    // Supplier
    {
      id: "supplier",
      content: (
        <span className="text-neutral-primary text-sm">
          {document.supplier?.name || document.company?.name || "-"}
        </span>
      ),
    },

    // Document Date (clickable to edit)
    {
      id: "documentDate",
      content: (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlers.onDateClick?.(document);
          }}
          className="text-neutral-primary cursor-pointer text-sm hover:underline hover:text-moss-primary transition-colors"
          type="button"
        >
          {formatDate(document.documentDate)}
        </button>
      ),
    },

    // Status (clickable to edit)
    {
      id: "status",
      content: (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlers.onStatusClick?.(document);
          }}
          className={`cursor-pointer rounded border px-2 py-1 text-xs font-medium transition-opacity hover:opacity-80 ${getStatusColor(
            document.status,
          )}`}
          type="button"
        >
          {document.status}
        </button>
      ),
    },

    // Odoo reconciliation status — only meaningful for imported documents.
    // Shows existence AND whether the bedrag still equals the invoice in Odoo.
    {
      id: "odooStatus",
      content:
        document.existsInOdoo === undefined ? (
          <span className="text-neutral-secondary-mid text-xs">—</span>
        ) : !document.existsInOdoo ? (
          <span
            className="rounded border border-orange-200 bg-orange-50 px-2 py-1 text-xs font-medium text-orange-700"
            title="Niet meer aanwezig in Odoo of gewijzigd sinds import"
          >
            Niet meer in Odoo
          </span>
        ) : document.amountMatchesOdoo === false ? (
          <span
            className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700"
            title="Het bedrag in YallyHub wijkt af van de factuur in Odoo"
          >
            ⚠ Bedrag wijkt af
          </span>
        ) : (
          <span
            className="rounded border border-green-200 bg-green-50 px-2 py-1 text-xs font-medium text-green-700"
            title="Aanwezig in Odoo en het bedrag komt overeen"
          >
            ✓ In Odoo
          </span>
        ),
    },

    // Betaald — Odoo payment status (account.move.payment_state), synced daily.
    {
      id: "payment",
      content: (() => {
        const cfg = PAYMENT_STATUS_CONFIG[document.paymentStatus ?? ""] ?? null;
        if (!cfg) {
          return <span className="text-neutral-secondary-mid text-xs">—</span>;
        }
        const checked = document.paymentStatusUpdatedAt
          ? ` · gecontroleerd op ${formatDate(document.paymentStatusUpdatedAt)}`
          : "";
        return (
          <span
            className={`rounded border px-2 py-1 text-xs font-medium ${cfg.className}`}
            title={`${cfg.title}${checked}`}
          >
            {cfg.label}
          </span>
        );
      })(),
    },

    // ─── Bedragen: excl btw / btw % / btw / incl btw ──────────────────────
    // Odoo boekt btw op aparte regels, dus totalExclVat is het echte
    // factuurbedrag excl btw. Zie shared/utils/invoiceVat voor de regels.
    {
      id: "amountExclVat",
      content: (
        <span
          className={`text-sm ${amountClass}`}
          title={isCreditNote ? "Creditnota — verlaagt de renovatiekost" : undefined}
        >
          {formatCurrency(signed(vat.exclVat))}
        </span>
      ),
    },
    {
      id: "vatPercentage",
      content: (
        <span
          className={`text-sm ${vat.isUnknown ? "text-neutral-secondary-mid" : "text-neutral-primary"}`}
          title={
            vat.isUnknown
              ? "Geen btw-code op de factuurregel in Odoo — btw onbekend"
              : vat.percentageTitle
          }
        >
          {vat.percentageLabel}
        </span>
      ),
    },
    {
      id: "vatAmount",
      content: (
        <span className={`text-sm ${amountClass}`}>
          {vat.isUnknown ? VAT_UNKNOWN : formatCurrency(signed(vat.vatAmount))}
        </span>
      ),
    },
    {
      id: "amount",
      content: (
        <div className="flex flex-col gap-1">
          <span
            className={`text-sm font-semibold ${amountClass}`}
            title={isCreditNote ? "Creditnota — verlaagt de renovatiekost" : undefined}
          >
            {formatCurrency(signed(totalAmount))}
          </span>
          <VatWarningBadge warning={vat.warning} />
        </div>
      ),
    },

    // Building (Gebouw)
    {
      id: "building",
      content: building ? (
        <div className="text-neutral-primary flex items-center gap-1.5 text-sm">
          <Building size={14} weight="duotone" className="text-brick-primary" />
          <span className="font-medium">
            {building.buildingOdooId || building.id}
          </span>
        </div>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      ),
    },

    // Units (Kostenplaatsen). `percentage` here is already in 0-100 display space.
    // Kleur = klopt de kostenplaats van de boekhouding met wat het
    // renovatieproject voorziet (groen) of niet (rood). Grijs blijft grijs
    // wanneer er niets te vergelijken valt — zie `matchStatus` hierboven.
    // When nothing could be derived but a project part is linked, show a CTA
    // that opens the part page in a new tab so the user can configure its
    // budget/unit distribution.
    {
      id: "units",
      content:
        analyticDistributions.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {analyticDistributions.slice(0, 3).map((d) => {
              const displayPct = d.percentage.toFixed(d.percentage < 10 ? 1 : 0);
              const status = matchStatus(d.unitId, d.sources);
              return (
                <span
                  key={d.unitId}
                  className={`rounded border px-2 py-0.5 text-xs ${MATCH_STYLE[status]}`}
                  title={`${d.unitName} · ${displayPct}% — ${describeSources(d.sources)} — ${MATCH_TOOLTIP[status]}`}
                >
                  {d.unitName} · {displayPct}%
                </span>
              );
            })}
            {analyticDistributions.length > 3 && (
              <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs text-neutral-secondary-mid">
                +{analyticDistributions.length - 3}
              </span>
            )}
            {nietGeboekt.length > 0 && (
              <span
                className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-800"
                title={`Voorzien op het renovatieproject, maar de boekhouding boekt er niets op: ${nietGeboekt
                  .map(([, label]) => label)
                  .join(", ")}`}
              >
                {nietGeboekt.length} voorzien, niet geboekt
              </span>
            )}
          </div>
        ) : missingKostenplaatsenCta ? (
          <a
            href={missingKostenplaatsenCta.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-800 hover:bg-amber-100 hover:underline"
            title={`Kostenplaatsen ontbreken op onderdeel ${missingKostenplaatsenCta.label} — klik om in te stellen`}
          >
            Kostenplaatsen instellen →
          </a>
        ) : (
          <span className="text-sm text-neutral-secondary-mid">-</span>
        ),
    },

    // Project — anchor opens the project detail in a new tab.
    {
      id: "project",
      content:
        primaryProject && primaryProject.id ? (
          <a
            href={`/renovation/${primaryProject.id}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-moss-primary flex cursor-pointer items-center gap-1.5 text-sm hover:underline"
            title={`Open project RENO-${primaryProject.id} in nieuw tabblad`}
          >
            <Building size={14} weight="duotone" />
            <span>
              RENO-{primaryProject.id}
              {primaryProject.title ? ` - ${primaryProject.title}` : ""}
            </span>
          </a>
        ) : (
          <span className="text-sm text-gray-400">-</span>
        ),
    },

    // Part (Projectonderdeel) — anchor opens the part detail in a new tab.
    {
      id: "part",
      content: (() => {
        if (!primaryPart) {
          return <span className="text-sm text-gray-400">-</span>;
        }
        const partProjectId =
          primaryPart.project?.id ||
          primaryPart.projectId ||
          primaryPart.renovationProjectId;
        const hasRoute = !!(partProjectId && primaryPart.id);
        const label = `${
          hasRoute ? `RENO-${partProjectId}-O${primaryPart.id}` : ""
        }${hasRoute && primaryPart.title ? " - " : ""}${primaryPart.title || "Onderdeel"}`;

        if (hasRoute) {
          return (
            <a
              href={`/renovation/${partProjectId}/part/${primaryPart.id}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-moss-primary flex cursor-pointer items-center gap-1.5 text-sm hover:underline"
              title={`Open onderdeel RENO-${partProjectId}-O${primaryPart.id} in nieuw tabblad`}
            >
              <SquaresFour size={14} weight="duotone" />
              <span className="truncate">{label}</span>
            </a>
          );
        }
        return (
          <div className="text-neutral-primary flex items-center gap-1.5 text-sm">
            <SquaresFour
              size={14}
              weight="duotone"
              className="text-moss-primary"
            />
            <span className="truncate" title={primaryPart.title}>
              {label}
            </span>
          </div>
        );
      })(),
    },

    // Bestelbon ↔ facturen. Een bestelbon wordt vaak in schijven gefactureerd, dus:
    //  - op een factuur staat de bestelbon waar ze bij hoort (klikbaar om te koppelen);
    //  - op een bestelbon staat hoeveel er al op gefactureerd is.
    {
      id: "purchaseOrder",
      content: (() => {
        if (document.type === "Bestelbon") {
          const count = Number(document.purchaseOrderInvoiceCount ?? 0) || 0;
          if (count === 0) {
            return (
              <span
                className="text-sm text-gray-400"
                title="Nog geen factuur aan deze bestelbon gekoppeld"
              >
                Nog niet gefactureerd
              </span>
            );
          }
          return (
            <span
              className="text-neutral-primary text-sm"
              title={`${count} gekoppelde factu${count === 1 ? "ur" : "ren"}, samen ${formatCurrency(
                Number(document.purchaseOrderInvoicedAmount ?? 0) || 0,
              )} excl. btw`}
            >
              {count} factu{count === 1 ? "ur" : "ren"} ·{" "}
              {formatCurrency(Number(document.purchaseOrderInvoicedAmount ?? 0) || 0)}
            </span>
          );
        }

        // Offertes en contracten hangen niet aan een bestelbon.
        if (document.type !== "Factuur") {
          return <span className="text-sm text-gray-400">-</span>;
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handlers.onPurchaseOrderClick?.(document);
            }}
            title={
              document.purchaseOrderNumber
                ? `Gekoppeld aan bestelbon ${document.purchaseOrderNumber} — klik om te wijzigen`
                : "Klik om een bestelbon te koppelen"
            }
            className={`flex w-full items-center gap-1.5 truncate rounded-sm border px-1.5 py-1 text-left text-sm transition-colors ${
              document.purchaseOrderNumber
                ? "text-neutral-primary border-neutral-200 hover:border-moss-primary/40"
                : "border-dashed border-neutral-300 text-gray-400 hover:border-moss-primary/40"
            }`}
          >
            <Receipt size={14} weight="duotone" className="text-moss-primary shrink-0" />
            <span className="truncate">
              {document.purchaseOrderNumber ?? "+ Bestelbon"}
            </span>
          </button>
        );
      })(),
    },

    // Offerte ↔ facturen. Dezelfde vorm als de bestelbon hierboven, want een
    // goedgekeurde offerte wordt net zo goed in schijven gefactureerd (offerte
    // € 7.800 → eerste factuur € 3.900). Het verschil: op een offerte staat er ook
    // hoeveel van het offertebedrag al gefactureerd is — dat is net de vergelijking
    // die zonder deze koppeling nergens te maken viel, omdat een offerte geen
    // factuurnummer draagt om automatisch op te matchen.
    {
      id: "quote",
      content: (() => {
        if (document.type === "Offerte") {
          const count = Number(document.quoteInvoiceCount ?? 0) || 0;
          if (count === 0) {
            return (
              <span
                className="text-sm text-gray-400"
                title="Nog geen factuur aan deze offerte gekoppeld"
              >
                Nog niet gefactureerd
              </span>
            );
          }
          const gefactureerd = Number(document.quoteInvoicedAmount ?? 0) || 0;
          // `totalAmount` is het offertebedrag van deze rij zelf. Enkel een
          // percentage tonen wanneer er een offertebedrag is om tegen af te zetten:
          // "50% van € 0" zegt niets.
          const aandeel =
            totalAmount > 0 ? Math.round((gefactureerd / totalAmount) * 100) : null;
          return (
            <span
              className="text-neutral-primary text-sm"
              title={`${count} gekoppelde factu${count === 1 ? "ur" : "ren"}, samen ${formatCurrency(
                gefactureerd,
              )} van het offertebedrag ${formatCurrency(totalAmount)}`}
            >
              {formatCurrency(gefactureerd)}
              {aandeel !== null && (
                <span className="text-neutral-secondary-mid"> · {aandeel}%</span>
              )}
            </span>
          );
        }

        // Bestelbonnen en contracten hangen niet aan een offerte.
        if (document.type !== "Factuur") {
          return <span className="text-sm text-gray-400">-</span>;
        }

        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handlers.onQuoteClick?.(document);
            }}
            title={
              document.quoteNumber
                ? `Gekoppeld aan offerte ${document.quoteNumber} — klik om te wijzigen`
                : "Klik om een offerte te koppelen"
            }
            className={`flex w-full items-center gap-1.5 truncate rounded-sm border px-1.5 py-1 text-left text-sm transition-colors ${
              document.quoteNumber
                ? "text-neutral-primary border-neutral-200 hover:border-moss-primary/40"
                : "border-dashed border-neutral-300 text-gray-400 hover:border-moss-primary/40"
            }`}
          >
            <FileText size={14} weight="duotone" className="text-moss-primary shrink-0" />
            <span className="truncate">{document.quoteNumber ?? "+ Offerte"}</span>
          </button>
        );
      })(),
    },

    // Renovatieticket — de tegenhanger van de OPEX-koppeling bij de helpdesk: hieruit
    // haalt het ticket zijn goedgekeurd offertebedrag en factuurbedrag.
    {
      id: "renoTicket",
      content: (
        <div className="flex min-w-0 items-center gap-1">
          {document.renoTicketId && (
            <a
              href={`/renotickets/${document.renoTicketId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-moss-primary flex shrink-0 cursor-pointer items-center gap-1 text-sm hover:underline"
              title={`Open renovatieticket RS-${document.renoTicketId} in nieuw tabblad`}
            >
              <Receipt size={14} weight="duotone" />
              RS-{document.renoTicketId}
            </a>
          )}
          {/* Koppelen kan nu ook hier, niet enkel op het ticket zelf: dit is de plek
              waar je de factuur voor je hebt. */}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handlers.onRenoTicketClick?.(document);
            }}
            title={
              document.renoTicketId
                ? "Koppeling wijzigen of losmaken"
                : "Renovatieticket koppelen"
            }
            className={`min-w-0 truncate rounded-sm border px-1.5 py-1 text-left text-sm transition-colors ${
              document.renoTicketId
                ? "text-neutral-secondary-mid border-neutral-200 hover:border-moss-primary/40"
                : "hover:border-moss-primary/40 border-dashed border-neutral-300 text-gray-400"
            }`}
          >
            {document.renoTicketId ? "Wijzig" : "+ Ticket"}
          </button>
        </div>
      ),
    },

    // Responsible User (Verantwoordelijke)
    {
      id: "responsible",
      content: responsibleUserId ? (
        <div className="flex items-center gap-2">
          <Avatar
            src={getUserImageUrlById(responsibleUserId, users)}
            name={getUserName(responsibleUserId, users).name || "Unknown User"}
            size="sm"
            className="shrink-0"
          />
          <span className="text-neutral-primary truncate text-sm">
            {getUserName(responsibleUserId, users).name || "Unknown User"}
          </span>
        </div>
      ) : (
        <span className="text-sm text-gray-400">-</span>
      ),
    },

    // Delete Action
    {
      id: "actions",
      content: (
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDeleting && handlers.onDeleteClick) {
              handlers.onDeleteClick(document);
            }
          }}
          disabled={isDeleting}
          className={`p-2 rounded-sm transition-colors ${
            isDeleting
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "hover:bg-red-50 text-red-500 hover:text-red-600"
          }`}
          type="button"
          title={isDeleting ? "Bezig met verwijderen..." : "Verwijder document"}
        >
          {isDeleting ? (
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
          ) : (
            <Trash size={16} weight="regular" />
          )}
        </button>
      ),
    },

  ];
};
